import type { MouseEvent } from 'react';

import { scrollToElement } from '@/utils';

export type Functionality =
  | 'book_appointment'
  | 'book_appointment_forms'
  | 'send_message'
  | 'enquire_now'
  | 'show_form'
  | 'external_link'
  | 'internal_link'
  | 'call'
  | 'custom';

type PrimaryButtonClickEvent = MouseEvent<
  HTMLButtonElement | HTMLAnchorElement
>;

export type PrimaryButtonFunctionalityArgs = {
  functionality?: Functionality;
  slug?: string;
  onShowForm?: () => void;
  onEnquireNow?: () => void;
};

export type PrimaryButtonProps = {
  href?: string;
  onClick?: (event: PrimaryButtonClickEvent) => void;
};

const normalizeValue = (value?: string): string | undefined => {
  const trimmedValue = value?.trim();
  return trimmedValue || undefined;
};

const createScrollHandler =
  (elementId: string) => (event: PrimaryButtonClickEvent) => {
    event.preventDefault();
    scrollToElement(elementId);
  };

const createTelHref = (slug?: string): string | undefined => {
  const phone = normalizeValue(slug);

  if (!phone) {
    return undefined;
  }

  if (phone.startsWith('tel:')) {
    return phone;
  }

  return `tel:${phone.replace(/\s+/g, '')}`;
};

export default function getPrimaryButtonProps({
  functionality,
  slug,
  onShowForm,
  onEnquireNow,
}: PrimaryButtonFunctionalityArgs): PrimaryButtonProps {
  const normalizedSlug = normalizeValue(slug);

  if (!functionality) {
    return {};
  }

  switch (functionality) {
    case 'call': {
      const href = createTelHref(normalizedSlug);
      return href ? { href } : {};
    }

    case 'book_appointment':
      return {
        href: '#booking-calendar-widget',
        onClick: createScrollHandler('booking-calendar-widget'),
      };

    case 'book_appointment_forms':
      return {
        href: '#booking_form',
        onClick: createScrollHandler('booking_form'),
      };

    case 'show_form':
      return {
        onClick: () => {
          onShowForm?.();
        },
      };

    case 'send_message':
      return {
        href: '/contact-us#message_form',
      };

    case 'enquire_now':
      return onEnquireNow
        ? {
            onClick: () => {
              onEnquireNow();
            },
          }
        : {
            href: '/enquire#form',
          };

    case 'external_link':
    case 'internal_link':
    case 'custom':
      return normalizedSlug ? { href: normalizedSlug } : {};

    default:
      return {};
  }
}
